/**
 * The marks this screen draws and the app does not already have: the cog, the undo arrow, the
 * restore arrow, the way back, the `⋯`, the caret on the term control, and the two week-strip verdicts.
 *
 * Everything else is imported — `CheckIcon`, `ElapsedMarkIcon`, `CrossIcon`, `ChevronRightIcon`,
 * `DoneMarkIcon` and the queue feature's own task glyphs all already ship. `docs/design/README.md`'s
 * rule is one drawing per concept, and a second clock on this screen would be a trainer meeting a
 * different clock here than on the client detail.
 *
 * Traced from `docs/design/zadania-2026-09-18/zadania-c-runda5.html` at the house stroke weight on a
 * 24-unit box. Every one of them is DECORATIVE: the control around it carries the name.
 */

interface IconProps {
  readonly className?: string;
}

/**
 * The box every glyph in this file is drawn in — 24 units, stroke 1.8, round joins, decorative.
 *
 * `d` is the common case, one path; `children` is for the two that need a `<circle>` beside their
 * paths. Both, rather than only `children`, because a one-path glyph reading `d="…"` is what every
 * other icon file in this app looks like.
 */
function Stroke({
  className,
  d,
  children,
}: IconProps & { readonly d?: string; readonly children?: React.ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {d === undefined ? null : <path d={d} />}
      {children}
    </svg>
  );
}

/**
 * The settings gear — the mock's own `P.gear`, transcribed path for path.
 *
 * The first two passes drew a ring with eight radiating spokes, which reads as a SUN and which the
 * owner called out twice. A gear is a toothed OUTLINE with a hub inside it: the teeth are part of
 * the body's silhouette rather than rays leaving it, and that is the whole difference between the
 * two drawings at 17px.
 *
 * The path reaches 1 and 23 on a 24 box, so a 1.8 stroke clips about 0.9 of a unit at four points.
 * That is the specimen's own geometry and Feather's; correcting it would make this the one gear in
 * the app that is not the shape everybody recognises.
 */
export function CogIcon({ className }: IconProps) {
  return (
    <Stroke
      className={className}
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"
    >
      <circle cx="12" cy="12" r="3" />
    </Stroke>
  );
}

/** Take the last decision back. An arrow turning left into a loop. */
export function UndoIcon({ className }: IconProps) {
  return <Stroke className={className} d="M9 7 4.5 11.5 9 16M4.5 11.5h9a5.5 5.5 0 0 1 0 11H10" />;
}

/** Back to the default — a level's „Przywróć …". An arrow closing a circle, not the undo's loop. */
export function RestoreIcon({ className }: IconProps) {
  return <Stroke className={className} d="M4.5 5v5h5M4.9 14.5a7.5 7.5 0 1 0 1.3-7.3L4.5 10" />;
}

/** The way back to the queue, on the kinds screen's crumb. */
export function BackIcon({ className }: IconProps) {
  return <Stroke className={className} d="M14.5 6 8.5 12l6 6" />;
}

/** The caret on the term control: this button opens something under it. */
export function CaretIcon({ className }: IconProps) {
  return <Stroke className={className} d="M7 10l5 5 5-5" />;
}
